import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Share2, Award } from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl, genesisShareUrl, shareLink } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuraRing } from "@/components/brand/AuraRing";
import { cn } from "@/lib/utils";

const RANK = ["text-aura-gold", "text-aura-cyan", "text-aura-magenta"];

// Public "Genesis Ambassadors" leaderboard (Market + /genesis). Recognition only — never tokens or money.
export function AmbassadorBoard({ compact = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => { api.genesisAmbassadors().then(setD).catch(() => setD(false)); }, []);
  if (!d) return null;
  const share = async () => {
    const url = genesisShareUrl(user?.username);
    const r = await shareLink(url, "AURAMAX GENESIS");
    if (r === "copied") toast("Your Genesis link is copied"); else if (r === "failed") toast(url);
  };
  return (
    <section data-testid="ambassador-board" className="mt-4 rounded-3xl border border-am-border bg-am-surface p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div><h2 className="flex items-center gap-2 text-base font-bold text-white md:text-lg"><Award className="h-4 w-4 text-aura-gold" /> Genesis Ambassadors {d.final && <span className="rounded-full border border-am-border px-2 py-0.5 text-[10px] font-bold uppercase text-am-text2" data-testid="ambassador-final">Final</span>}</h2>
          <p className="text-xs text-am-text2">Members bringing real visitors to Genesis. Badge at {d.milestones[0]} visitors · Aura +{d.bonuses.join(" / +")} at {d.milestones.join(" / ")}.</p></div>
        {user && !d.final && <button onClick={share} data-testid="ambassador-share" className="inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-2 text-xs font-bold text-black hover:scale-[1.03]"><Share2 className="h-3.5 w-3.5" /> Share my Genesis link</button>}
      </div>
      {d.rows.length === 0 ? <p className="mt-3 rounded-xl border border-am-border bg-am-bg/50 p-3 text-sm text-am-text2" data-testid="ambassador-empty">No ambassadors yet — share your link to be first.</p> : (
        <div className="mt-3 divide-y divide-am-border rounded-2xl border border-am-border">
          {d.rows.slice(0, compact ? 5 : 10).map((r) => (
            <button key={r.username} onClick={() => navigate(`/@${r.username}`)} data-testid={`ambassador-row-${r.username}`} className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-white/5">
              <span className={cn("w-5 text-center font-mono text-sm font-bold", RANK[r.rank - 1] || "text-am-text3")}>{r.rank}</span>
              <AuraRing src={mediaUrl(r.picture) || undefined} size={32} ring={1.5} fallback={r.name} />
              <div className="min-w-0 flex-1"><div className="truncate text-sm font-semibold text-white">{r.name}</div><div className="truncate text-xs text-am-text2">@{r.username}</div></div>
              <div className="text-right text-xs"><div className="font-bold text-white">{r.unique_visitors} visitors</div><div className="hidden text-am-text3 sm:block">{r.buy_clicks} buy clicks</div></div>
            </button>
          ))}
        </div>
      )}
      {d.me && <p className="mt-2 text-xs text-am-text2" data-testid="ambassador-me">You: {d.me.unique_visitors} unique visitors · {d.me.buy_clicks} buy clicks{d.me.rank ? ` · rank #${d.me.rank}` : ""}</p>}
      <p className="mt-2 text-[11px] text-am-text3">Recognition only — no tokens, SOL or allocations. Buying happens on Smithii under its terms.</p>
    </section>
  );
}
